import { Form, Row } from 'antd';
import React, { useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import FormWrapper from 'components/molecules/FormWrapper';
import FsluzbyIdentifikace from 'components/molecules/StatniSprava/Identifikace';

import { useDateLimit } from 'hooks/statniSprava/useDateLimit';
import { useFormBasics } from 'hooks/statniSprava/useFormBasics';

import typOsobySelect from 'data/codebooks/specialniSluzby/typOsobySelect';

import isinValidation from 'utils/validations/isinValidation';
import numberValidation from 'utils/validations/numberValidation';
import { rcmaValidationSimple } from 'utils/validations/rcmaValidation';

import AlertComponent from './AlertComponent';
import IsinTable from './IsinTable';
import OdeslaniPozadavkuRows from './odeslaniPozadavkuRows';

interface IF27 {}

const F27: React.FC<IF27> = () => {
    const [form] = Form.useForm();
    const [, dateLoading, commonDisabledDate] = useDateLimit();
    const [showIsinTable, setShowIsinTable] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);

    const [
        errMsg,
        currentStep,
        idSluzby,
        upozorneni,
        loading,
        odeslaniResp,
        setErrMsg,
        prev,
        onFormSubmit,
        showPxKlicLoadingMsg
    ] = useFormBasics();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const dataToSend = {
            ...fieldsValue,
            dtod: fieldsValue.dtod.format('YYYY-MM-DD'),
            dtdo: fieldsValue.dtdo.format('YYYY-MM-DD'),
            ...(fieldsValue.idmb &&
                typeof fieldsValue.idmb === 'object' && {
                    idmb: fieldsValue.idmb.format('YYYYMMDD')
                })
        };
        onFormSubmit(dataToSend);
    };
    return (
        // page140b
        <LoadingWrapper loading={loading} loadingText={showPxKlicLoadingMsg ? 'LOADING_PXKLIC' : 'NACITANI'}>
            <Title level={4} text="DUVERNE_INFO_TITLE_F27" />{' '}
            {errMsg ? (
                <>
                    <Paragraph text={errMsg} />
                    <Row>
                        <ButtonComponent
                            id="f27ZpetBtn"
                            type="default"
                            buttontext="ZPET"
                            onClick={() => setErrMsg('')}
                        />
                    </Row>
                </>
            ) : (
                <>
                    {currentStep !== 2 ? (
                        <FormWrapper onFinish={onFinish} outForm={form}>
                            {showIsinTable ? (
                                <IsinTable upperForm={form} setShowIsinTable={setShowIsinTable} formIsinName={'cval'} />
                            ) : (
                                <>
                                    <LoadingWrapper loading={dateLoading}>
                                        <DatePickerComponent
                                            name="dtod"
                                            label="ME_POZADAVKY_DATUM1"
                                            disabled={currentStep > 0}
                                            disabledDate={current =>
                                                current > form.getFieldValue('dtdo') || commonDisabledDate(current)
                                            }
                                        />
                                        <DatePickerComponent
                                            name="dtdo"
                                            disabled={currentStep > 0}
                                            label="ME_POZADAVKY_DATUM2"
                                            disabledDate={current =>
                                                current < form.getFieldValue('dtod') || commonDisabledDate(current)
                                            }
                                        />
                                    </LoadingWrapper>
                                    <InputComponent
                                        label="ME_POZADAVKY_CISLO_JEDNACI"
                                        name="actn"
                                        maxLength={30}
                                        disabled={currentStep > 0}
                                    />
                                    <SelectComponent
                                        disabled={currentStep > 0}
                                        name="octg"
                                        labelParagraph="ME_POZADAVKY_KATEGORIE_MAJITELE"
                                        labelCol={{
                                            span: 8
                                        }}
                                        defaultValue={typOsobySelect[0].value}
                                        data={typOsobySelect}
                                        isFormItem={true}
                                        onChange={() => {
                                            form.setFieldsValue({
                                                identifikace: undefined
                                            });
                                            rerender(pv => !pv);
                                        }}
                                    />
                                    <FsluzbyIdentifikace form={form} disabled={currentStep > 0} rerender={rerender} />
                                    <InputComponent
                                        label="ME_POZADAVKY_PREDCISLI_UCTU"
                                        name="cdac"
                                        type="text"
                                        maxLength={4}
                                        rules={numberValidation()}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('cdac')}
                                        disabled={currentStep > 0}
                                    />
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label="CISLO_UCTU"
                                        name="rcma"
                                        type="text"
                                        rules={rcmaValidationSimple()}
                                        notRequired={true}
                                        maxLength={12}
                                    />
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label="ISIN"
                                        name="cval"
                                        type="text"
                                        maxLength={12}
                                        rules={isinValidation()}
                                        capitalize={true}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('cval')}
                                    />
                                    {currentStep === 0 && (
                                        <ButtonComponent
                                            id="f27IsinBtn"
                                            disabled={currentStep > 0}
                                            buttontext="DUVERNE_INFO_ISIN_BUTTON"
                                            onClick={() => setShowIsinTable(true)}
                                            style={{
                                                position: 'relative',
                                                top: -12
                                            }}
                                        />
                                    )}
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label="ME_POZADAVKY_PRIJMENO"
                                        name="pjma"
                                        type="text"
                                        maxLength={250}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('pjma')}
                                    />
                                    <InputComponent
                                        disabled={currentStep > 0}
                                        label="ME_POZADAVKY_JMENO"
                                        name="jmma"
                                        type="text"
                                        maxLength={250}
                                        notRequired={true}
                                        hide={currentStep === 1 && !form.getFieldValue('jmma')}
                                    />
                                    {currentStep === 0 && (
                                        <ButtonComponent
                                            id="f27OdeslatBtn"
                                            buttontext="ODESLAT"
                                            onClick={() => form.submit()}
                                        />
                                    )}
                                    {currentStep !== 0 && upozorneni && (
                                        <AlertComponent
                                            upozorneni={upozorneni}
                                            type={currentStep === 1 ? 'error' : 'info'}
                                        />
                                    )}
                                    {currentStep === 1 && (
                                        <Row className="button-row">
                                            <ButtonComponent
                                                id="f27ZpetAltBtn"
                                                buttontext="ZPET"
                                                onClick={() => prev()}
                                                type="default"
                                            />

                                            <ButtonComponent
                                                id="f27PotvrditBtn"
                                                buttontext="POTVRDIT"
                                                onClick={() => form.submit()}
                                            />
                                        </Row>
                                    )}
                                </>
                            )}
                        </FormWrapper>
                    ) : (
                        <>
                            <OdeslaniPozadavkuRows idSluzby={idSluzby} datum={odeslaniResp?.datum} />
                            {upozorneni && <AlertComponent upozorneni={upozorneni} type={'info'} />}
                        </>
                    )}{' '}
                </>
            )}
        </LoadingWrapper>
    );
};

export default F27;
